import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { defaultRange } from "../components/DateRangePicker";
import { DataTable, type Column } from "../components/DataTable";
import { useTableQuery } from "../api/useTableQuery";
import { QueryError } from "../components/QueryError";
import { Modal } from "../components/Modal";
import { JsonView } from "../components/JsonView";
import { ReportView, OutcomeView, type ReportDocument } from "../components/ReportView";
import { apiFetch, apiFetchBlob, saveBlob, ApiError } from "../api/client";
import { WithdrawalDialog } from "../components/WithdrawalDialog";
import { recordWithdrawal, type WithdrawalBody, getDeletionPreview, deleteFromFintrac,
         type DeletionBody } from "../api/portal";
import { FintracDeletionDialog } from "../components/FintracDeletionDialog";
import { money, dateTime } from "../format";

interface ReportSummary {
  withdrawn_at?: string | null;
  candidate_id: string;
  report_type: string;
  status: string;
  fintrac_reference_num: string | null;
  detected_at: string;
  deadline_date: string | null;
  total_amount_cad: string | null;
  transaction_count: number;
  // The latest FINTRAC POST, whatever its outcome -- set for a rejected or
  // errored report too, null only if the report was never sent.
  fintrac_submitted_at: string | null;
  first_submitted_at: string | null;
  submission_count: number;
}

/** The latest submission; a tooltip names the first when there were several,
 *  since the latest is the one the status and FINTRAC reference describe. */
function submittedCell(r: ReportSummary) {
  if (!r.fintrac_submitted_at) return "—";
  const latest = dateTime(r.fintrac_submitted_at);
  if (r.submission_count > 1 && r.first_submitted_at) {
    return (
      <span title={`${r.submission_count} attempts, first ${dateTime(r.first_submitted_at)}`}>
        {latest}
      </span>
    );
  }
  return latest;
}

interface ReportDetail extends ReportSummary {
  report_download_url: string | null;
  fintrac_response_download_url: string | null;
  report_json: unknown;
  report_json_error: string | null;
  report_view: ReportDocument | null;
  withdraw_reason: string | null;
  withdrawn_note: string | null;
  /** Server-computed: FINTRAC accepted it and it is not yet withdrawn. */
  withdrawable: boolean;
  /** Server-computed: the caller is a Customer Admin and may delete it at
   *  FINTRAC now (spec 2026-10-09). Always false for staff. */
  deletable?: boolean;
  deletable_until?: string | null;
  withdraw_method?: string | null;
}

function statusLabel(status: string): string {
  return status === "WITHDRAWING" ? "Deleting…" : status;
}

function pillClass(status: string): string {
  if (status === "ACCEPTED") return "pill ok";
  if (status === "REJECTED" || status === "ERROR") return "pill bad";
  // Filed, then deleted at FINTRAC: neither an error nor a live filing, but
  // it must stand out -- the report is no longer on file.
  if (status === "WITHDRAWN") return "pill withdrawn";
  // WITHDRAWING: a deletion at FINTRAC is in flight.
  if (status === "WITHDRAWING") return "pill mute";
  return "pill warn";
}

/** clientId is set only when an internal user is inside a customer's
 *  context; customer-role users are scoped by their own JWT. `internal`
 *  enables staff-only actions -- the backend enforces the same rule. */
export function ReportsPage({ clientId, internal = false }: { clientId?: string; internal?: boolean } = {}) {
  const [{ from, to }, setRange] = useState(defaultRange());
  // Two pieces of state, deliberately: `selected` is the last report opened
  // and outlives the modal, so the row stays marked after the viewer closes --
  // which is the one moment the highlight is useful, since an open modal
  // covers the list.
  const [selected, setSelected] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // Per-report UI state: a rejection badge or a stale export error from the
  // previously opened report must not carry over when a different report is
  // opened, so both reset alongside `selected`/`open` here rather than being
  // left to whatever they held from the last time the modal was open.
  const [tab, setTab] = useState<"report" | "outcome" | "raw">("report");
  const [exportError, setExportError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [withdrawing, setWithdrawing] = useState(false);
  const withdraw = useMutation({
    mutationFn: (body: WithdrawalBody) => recordWithdrawal(selected!, clientId!, body),
    onSuccess: () => {
      setWithdrawing(false);
      queryClient.invalidateQueries({ queryKey: ["report-detail"] });
      queryClient.invalidateQueries({ queryKey: ["/portal/reports"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/portal/transactions"] });
    },
  });
  const [deleting, setDeleting] = useState(false);
  const preview = useQuery({
    queryKey: ["deletion-preview", selected],
    queryFn: () => getDeletionPreview(selected!),
    enabled: deleting && !!selected,
    retry: false,
  });
  const del = useMutation({
    mutationFn: (body: DeletionBody) => deleteFromFintrac(selected!, body),
    onSuccess: () => {
      setDeleting(false);
      for (const key of [["report-detail"], ["/portal/reports"], ["dashboard"], ["/portal/transactions"]])
        queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const apiError = (e: unknown, fallback: string) =>
    e instanceof ApiError ? ((e.body as { error?: string })?.error ?? fallback) : e ? fallback : null;
  const withdrawError = withdraw.error instanceof ApiError
    ? ((withdraw.error.body as { error?: string })?.error ?? "the withdrawal could not be recorded")
    : withdraw.error ? "the withdrawal could not be recorded" : null;
  const openReport = (candidateId: string) => {
    setSelected(candidateId);
    setOpen(true);
    setTab("report");
    setExportError(null);
    setWithdrawing(false);
    withdraw.reset();
    setDeleting(false);
    del.reset();
  };

  const download = async (format: "pdf" | "csv") => {
    setExportError(null);
    try {
      const scope = clientId ? `&client_id=${encodeURIComponent(clientId)}` : "";
      const { blob, filename } = await apiFetchBlob(
        `/portal/reports/${selected}/export?format=${format}${scope}`,
      );
      saveBlob(blob, filename);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : null;
      setExportError(body?.error ?? "the download could not be produced");
    }
  };

  const q = useTableQuery<ReportSummary>("/portal/reports",
    clientId ? { from, to, client_id: clientId } : { from, to });

  const detailScope = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
  const detailQuery = useQuery({
    queryKey: ["report-detail", selected, clientId ?? null],
    queryFn: () => apiFetch<ReportDetail>(`/portal/reports/${selected}${detailScope}`),
    // Disabled while closed: `selected` deliberately outlives the modal (it
    // drives the row highlight), so without gating on `open` too this query
    // would never unmount -- refetching on window focus while closed, and
    // worse, serving a stale cached response (with an expired presigned URL)
    // on reopen instead of refetching.
    enabled: open && Boolean(selected),
  });
  const detail = detailQuery.data;
  // Nothing for any tab to show (no readable report body, no report_view):
  // hide the tab strip entirely rather than leave three clickable-but-inert
  // tabs above the lone "No report file was produced" message.
  const hasContent = Boolean(detail?.report_view) || detail?.report_json != null;

  const columns: Column<ReportSummary>[] = [
    { header: "Date (detected)", sortKey: "detected_at", filter: { kind: "dateRange" },
      render: (r) => dateTime(r.detected_at) },
    { header: "Submitted", sortKey: "fintrac_submitted_at", render: submittedCell },
    { header: "Type", sortKey: "report_type", filter: { kind: "select", param: "f_type" },
      render: (r) => (
        // A real button, not the row: the row's own onClick gives mouse users
        // a large target, and this gives everyone else a focusable control
        // without putting role="button" on a <tr>.
        <button
          className="link-btn"
          onClick={(e) => { e.stopPropagation(); openReport(r.candidate_id); }}
        >
          {r.report_type}
        </button>
      ) },
    { header: "Status", sortKey: "status", filter: { kind: "select", param: "f_status" },
      render: (r) => <span className={pillClass(r.status)}>{statusLabel(r.status)}</span> },
    { header: "Deadline", sortKey: "deadline_date", render: (r) => r.deadline_date ?? "—" },
    { header: "Amount CAD", sortKey: "total_amount_cad", numeric: true,
      render: (r) => money(r.total_amount_cad) },
    { header: "Txns", sortKey: "transaction_count", numeric: true,
      render: (r) => r.transaction_count },
    { header: "FINTRAC Ref", sortKey: "fintrac_reference_num",
      filter: { kind: "text", param: "f_ref" },
      render: (r) => r.fintrac_reference_num ?? "—" },
  ];

  return (
    <div>
      {q.isLoading && <p>Loading…</p>}
      {q.error && <QueryError error={q.error} what="reports" />}
      {!q.error && (
        <DataTable
          live={q.liveBinding}
          columns={columns}
          rows={q.items}
          total={q.total}
          limit={q.limit}
          offset={q.offset}
          onPageChange={q.setOffset}
          rowKey={(r) => r.candidate_id}
          onRowClick={(r) => openReport(r.candidate_id)}
          selectedRowKey={selected ?? undefined}
          sort={q.sort}
          dir={q.dir}
          onSortChange={q.setSort}
          filters={q.filters}
          onFilterChange={q.setFilter}
          onClearFilters={q.clearFilters}
          facets={{ f_type: q.facets.report_type ?? [], f_status: q.facets.status ?? [] }}
          dateRange={{
            from, to,
            // Narrowing the range can shrink `total` below the current
            // offset (e.g. offset=100 on page 3, then a 12-row range),
            // which would otherwise request a page that no longer exists.
            onChange: (f, t) => { setRange({ from: f, to: t }); q.setOffset(0); },
          }}
        />
      )}
      {open && selected && (
        <Modal title={`Report ${selected}`} size="wide" onClose={() => setOpen(false)}>
          <div className="modal-header">
            <h3>{detail ? `${detail.report_type} — ${detail.candidate_id}` : "Report"}</h3>
            <button className="modal-close" aria-label="Close" onClick={() => setOpen(false)}>
              ×
            </button>
          </div>
          {detail && (
            <p className="report-meta">
              <span className={pillClass(detail.status)}>{statusLabel(detail.status)}</span>
              <span>FINTRAC reference: {detail.fintrac_reference_num ?? "—"}</span>
              <span>Deadline: {detail.deadline_date ?? "—"}</span>
              <span>Submitted: {submittedCell(detail)}</span>
            </p>
          )}
          {!internal && detail?.deletable && detail.status === "WITHDRAWING" && (
            <p className="banner warn">
              Deletion at FINTRAC in progress — outcome unknown.{" "}
              <button onClick={() => { del.reset(); setDeleting(true); }}>Retry deletion</button>
            </p>
          )}
          {hasContent && (
            <div className="report-tabs" role="tablist">
              <button role="tab" aria-selected={tab === "report"} onClick={() => setTab("report")}>
                Report
              </button>
              <button role="tab" aria-selected={tab === "outcome"} onClick={() => setTab("outcome")}>
                FINTRAC outcome
                {(detail?.report_view?.outcome.issues.length ?? 0) > 0 && (
                  <span className="tab-badge">{detail!.report_view!.outcome.issues.length}</span>
                )}
              </button>
              <button role="tab" aria-selected={tab === "raw"} onClick={() => setTab("raw")}>
                Raw JSON
              </button>
            </div>
          )}
          <div className="modal-body">
            {detailQuery.isPending && <p>Loading report…</p>}
            {detailQuery.error && !detail && <QueryError error={detailQuery.error} what="this report" />}
            {detail?.report_json_error && <p className="empty">{detail.report_json_error}</p>}
            {detail != null && detail.report_json == null && !detail.report_json_error && (
              <p className="empty">No report file was produced for this candidate.</p>
            )}
            {tab === "report" && detail?.report_view && <ReportView document={detail.report_view} />}
            {tab === "outcome" && detail?.report_view && (
              <OutcomeView outcome={detail.report_view.outcome} />
            )}
            {tab === "raw" && detail?.report_json != null && <JsonView value={detail.report_json} />}
          </div>
          <div className="modal-actions report-actions">
            {detail?.report_view && (
              <>
                <button onClick={() => download("pdf")}>Download PDF</button>
                <button onClick={() => download("csv")}>Download CSV</button>
              </>
            )}
            {internal && clientId && detail?.withdrawable && (
              <button className="btn-danger" onClick={() => { withdraw.reset(); setWithdrawing(true); }}>
                Record FINTRAC withdrawal
              </button>
            )}
            {!internal && detail?.deletable && detail.status !== "WITHDRAWING" && (
              <span className="delete-action">
                <button className="btn-danger" onClick={() => { del.reset(); setDeleting(true); }}>
                  Delete from FINTRAC
                </button>
                {detail.deletable_until && <small>Can be deleted until {dateTime(detail.deletable_until)}</small>}
              </span>
            )}
            {!internal && !detail?.deletable && detail?.deletable_until
              && (detail.status === "SUBMITTED" || detail.status === "ACCEPTED") && (
              <small className="window-closed">
                Deletion window (72 h) closed {dateTime(detail.deletable_until)} · contact Scadox
              </small>
            )}
            <span className="raw-files">
              {detail?.report_json != null && (
                <button
                  onClick={() =>
                    navigator.clipboard
                      ?.writeText(JSON.stringify(detail.report_json, null, 2))
                      .catch(() => {})
                  }
                >
                  Copy JSON
                </button>
              )}
              {detail?.report_download_url && (
                <a href={detail.report_download_url}>Raw report</a>
              )}
              {detail?.fintrac_response_download_url && (
                <a href={detail.fintrac_response_download_url}>Raw FINTRAC response</a>
              )}
            </span>
          </div>
          {exportError && <p className="empty">Download failed — {exportError}.</p>}
          {withdrawing && (
            <WithdrawalDialog
              today={new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" })}
              busy={withdraw.isPending}
              error={withdrawError}
              onSubmit={(body) => withdraw.mutate(body)}
              onCancel={() => setWithdrawing(false)}
            />
          )}
          {deleting && preview.data && (
            <FintracDeletionDialog
              preview={preview.data}
              busy={del.isPending}
              error={apiError(del.error, "the report could not be deleted")}
              onSubmit={(body) => del.mutate(body)}
              onCancel={() => setDeleting(false)}
            />
          )}
          {deleting && preview.error && (
            <p className="empty">{apiError(preview.error, "the report cannot be deleted")}</p>
          )}
        </Modal>
      )}
    </div>
  );
}
